import React from 'react';
import * as R from 'ramda';
import { useLocation, useParams } from 'react-router-dom';
import { func, number, string } from 'prop-types';

// Components
import { Box } from '@mui/material';
import FilterIdentifier from './filter-identifier/FilterIdentifier';

// Utils
import {
  activeFilter,
  authorizationSelectionFilter,
  authorizationSequencingFilter,
  carrierFilter,
  exchangeFilter,
  extendedLoadingSelectionFilter,
  inventoryOwnerFilter,
  limitTypeFilter,
  loadingAllowedFilter,
  locationFilter,
  productFilter,
  stateFilter,
  statusFilter,
  supplierFilter,
  typeFilter,
} from '../filterFunctions';

const FilterIdentifierPanel = ({
  active,
  authorizationSelection,
  exchange,
  extendedLoadingSelection,
  filterIdentifierCount,
  inventoryOwner,
  limitType,
  loadingAllowed,
  location,
  nameShort,
  product,
  resetSingleFilter,
  state,
  status,
  supplier,
  type,
}) => {
  const { viewBy } = useParams();
  const { pathname } = useLocation();
  const authorizationType = pathname.split('/')[5];

  // Map object to correctly display only the necessary filters across modules
  // Necessary due to some similarly named properties, such as two filters in the same module using nameShort
  const filterIdentifierMap = {
    authorizationSequencing: [
      authorizationSequencingFilter(nameShort, viewBy),
      loadingAllowedFilter(loadingAllowed),
      stateFilter(state),
    ],
    carrierAuthorizations: [
      viewBy === 'carrier'
        ? locationFilter(nameShort, authorizationType)
        : carrierFilter(nameShort),
      stateFilter(state),
    ],
    myAuthorizations: [
      activeFilter(active),
      exchangeFilter(exchange),
      inventoryOwnerFilter(inventoryOwner),
      limitTypeFilter(limitType),
      locationFilter(location, authorizationType),
      productFilter(product),
      stateFilter(state),
    ],
    selfAuthorizations: [
      authorizationSelectionFilter(authorizationSelection),
      extendedLoadingSelectionFilter(extendedLoadingSelection),
    ],
    supplierAuthorizations: [
      exchangeFilter(exchange),
      typeFilter(type),
      statusFilter(status),
      supplierFilter(supplier),
      locationFilter(location, authorizationType),
      productFilter(product),
      stateFilter(state),
    ],
  };

  const renderFilterIdentifiers = R.compose(
    R.map(filter => (
      <FilterIdentifier
        key={filter.name}
        label={filter.label}
        name={filter.name}
        resetSingleFilter={resetSingleFilter}
        value={filter.value}
      />
    )),
    // Necessary to properly filter out "Any" and "All" due to different verbiage across modules
    R.reject(R.either(R.propEq('value', 'All'), R.propEq('value', 'Any')))
  );

  const camelCaseAuthorizationBy = R.compose(
    R.join(''),
    R.adjust(1, str => str[0].toUpperCase() + str.substr(1)),
    R.split('-')
  );

  return (
    <>
      {filterIdentifierCount ? (
        <Box m="-16px 0 8px 24px">
          {renderFilterIdentifiers(
            filterIdentifierMap[camelCaseAuthorizationBy(authorizationType)]
          )}
        </Box>
      ) : null}
    </>
  );
};

FilterIdentifierPanel.propTypes = {
  active: string.isRequired,
  authorizationSelection: string.isRequired,
  exchange: string.isRequired,
  extendedLoadingSelection: string.isRequired,
  filterIdentifierCount: number.isRequired,
  inventoryOwner: string.isRequired,
  limitType: string.isRequired,
  loadingAllowed: string.isRequired,
  location: string.isRequired,
  nameShort: string.isRequired,
  product: string.isRequired,
  resetSingleFilter: func.isRequired,
  state: string.isRequired,
  status: string.isRequired,
  supplier: string.isRequired,
  type: string.isRequired,
};

export default FilterIdentifierPanel;
